/**
 * Rodeo Wizard Store
 *
 * Multi-step form state for creating/editing rodeos with
 * performances, event configs, and round configs.
 *
 * Steps: 1) Basic Info → 2) Event Configs → 3) Performances → 4) Review
 *
 * (Capacity admin lives outside the wizard — `event_draw_capacity` /
 * per-perf overrides are configured elsewhere in the app.)
 */

import { create } from 'zustand';
import { httpClient } from '../api/http-client';
import {
  getDefaultTimezone,
  localInputToUtcIso,
  utcIsoToLocalInput,
  type UsTimezone,
} from '../utils/timezone';

// --- Types ---

export type PreferenceOption = 'slack' | 'out' | 'performance';
export interface PreferenceConfig {
  ifPerformance: PreferenceOption[];
  ifSlack: PreferenceOption[];
}

export const DEFAULT_PREFERENCE_CONFIG: PreferenceConfig = {
  ifPerformance: ['slack', 'out', 'performance'],
  ifSlack: ['slack', 'out', 'performance'],
};

export interface RodeoBasicInfo {
  name: string;
  organization_id: string;
  location: string;
  start_date: string;
  end_date: string;
  description: string;
  association: string;
  allow_cash_payment: boolean;
  /**
   * Rodeo-level Pay Later switch — the sibling of `allow_cash_payment`.
   * Defaults on (Pay Later has always been offered). Individual events can
   * override it via `EventDraft.allow_pay_later`.
   */
  allow_pay_later: boolean;
  /**
   * Marks this as a finals rodeo: every contestant of an event runs in
   * EVERY performance that event's rounds are tied to (Round 1 -> Friday's
   * perf, Round 2 -> Saturday's perf). Buddy groups and performance
   * preferences don't apply, and draws/run order use the finals branch.
   */
  is_finals: boolean;
  /**
   * Per-rodeo override of the organization's membership requirement.
   * `null` = inherit the org (the default), `true` = require membership for
   * this rodeo, `false` = open rodeo (everyone is treated as a member: no
   * non-member surcharge, no membership card, deferred payment options open
   * to all).
   */
  member_fee_enabled: boolean | null;
  non_member_surcharge: number;
  /**
   * Admin-controlled rules for the 2nd-performance-preference dropdown.
   * Always populated client-side (defaults to all options allowed) so the
   * checkbox UI in BasicInfoStep can render without null-checks.
   */
  preference_config: PreferenceConfig;
  /**
   * Required ISO-8601 datetime for when entries close — surfaced on the
   * registration wizard payment step. Stored as the local-time string
   * produced by <input type="datetime-local"> (e.g. "2026-05-15T18:00")
   * and shipped to the backend unchanged; the API layer accepts both
   * naive and zoned ISO strings.
   */
  entry_close_time: string;
  /**
   * Optional single-line note shown beneath the entry-close time on the
   * payment step. Empty string means "no message" — the backend coerces
   * blanks to NULL so the UI never renders an empty line.
   */
  fees_due_message: string;
  /**
   * IANA timezone (e.g. America/Los_Angeles) the rodeo's wall-clock
   * timestamps should be interpreted in. Constrained to the four US
   * zones the wizard offers (PT/MT/CT/ET) but typed as `string` so any
   * IANA value loaded off a legacy row still round-trips. Defaults to
   * the user's browser TZ mapped to the nearest US zone.
   */
  timezone: UsTimezone;
}

export interface PerformanceDraft {
  _key: string; // client-side key
  // True if the row was hydrated from the server on the most recent loadDraft.
  // Used so removePerformance can distinguish between "never saved, hard-remove"
  // and "persisted in DB, mark for deletion".
  _persisted?: boolean;
  // When true, the row is visually marked as pending deletion and will be
  // filtered out by saveDraft before sending to the backend. Toggled by the
  // delete button on persisted rows so the user can undo before saving.
  _pendingDelete?: boolean;
  // True when the row has local changes not yet persisted — either a brand-new
  // row added this session or a persisted row whose fields were edited. Cleared
  // by saveDraft after a successful round-trip.
  _pendingEdit?: boolean;
  performance_name: string;
  performance_date: string;
  start_time: string;
  /**
   * Legacy column — the dialog no longer collects it and new rows are
   * persisted with `end_time = null`. Kept nullable on the draft type
   * so we can still read a legacy value off a hydrated server row
   * without coercing it to `''`.
   */
  end_time: string | null;
  sort_order: number;
  is_slack: boolean;
  default_max_contestants: number | null;
}

/**
 * A single event (e.g. Breakaway, $1000 prize, 3 rounds, $50 entry) defined at
 * the rodeo level. The event can run in one or many performances via the
 * `performance_assignments` array — that's the change from the old
 * single-`performance_key` model. Each assignment can override the rodeo-level
 * `contestant_limit` with its own cap for that perf (e.g. Saturday caps 80,
 * Friday caps 60).
 */
export interface EventDraft {
  _key: string;
  _persisted?: boolean;
  _pendingDelete?: boolean;
  _pendingEdit?: boolean;
  /**
   * Optional custom display name. Empty string is treated the same as null —
   * downstream code falls back to `event_type` via `getEventDisplayTitle`.
   */
  event_name?: string;
  event_type: string;
  event_category: 'timed' | 'scored';
  contestant_limit: number | null;
  /**
   * Default contestants in any single performance (events.contestants_per_performance).
   * Persists alongside contestant_limit. NULL = no global default; the draw falls
   * back to splitting contestant_limit across the assigned performances. Per-perf
   * overrides still ride on `performance_assignments[i].max_contestants`.
   */
  contestants_per_performance: number | null;
  non_member_surcharge: number | null;
  entry_fee: number | null;
  transaction_fee: number | null;
  /**
   * Org-controlled per-contestant entry cap. When `allow_multiple_registrations`
   * is true, contestants may hold up to `max_registrations_per_user` active
   * registrations for this event; the backend rejects further submits with
   * MAX_REGISTRATIONS_REACHED once the cap is hit. Both default to off / 1.
   */
  allow_multiple_registrations?: boolean;
  max_registrations_per_user?: number | null;
  /**
   * Team roping only. When true, a contestant's entries for this event must
   * split as evenly as possible between Header and Heeler — the per-side cap
   * becomes ceil(max_registrations_per_user / 2). Defaults off.
   */
  enforce_balanced_positions?: boolean;
  /**
   * Per-event override of the rodeo's Pay Later switch. `null` (the default)
   * inherits `RodeoBasicInfo.allow_pay_later`; an explicit boolean wins in
   * either direction, including turning Pay Later off for this event alone.
   */
  allow_pay_later?: boolean | null;
  /**
   * M2M assignments into performances. Empty array is valid mid-wizard; the
   * user can define the event first and assign it to perfs later.
   */
  performance_assignments: EventPerformanceAssignment[];
  round_configs: RoundConfigDraft[];
  // Prize metadata — jackpot-parity, persisted via rodeo_events.metadata.prizes
  total_prize_amount?: number | null;
  prize_adjustments?: Array<{
    id: string;
    operation: 'add' | 'subtract';
    adjustment_type: 'amount' | 'percentage';
    amount: number;
    percentage: number;
    reason: string;
  }>;
  // Dynamic prize allocation + contestant-display settings (first-class
  // events columns). default_adjustment_overrides maps org-default
  // adjustment id → per-event { is_active, amount, percentage } override.
  prize_allocation_mode?: 'fixed' | 'dynamic';
  // Single per-entry contribution (dynamic mode only): a flat $ amount per
  // entry, a % of the entry fee when entry_fee_prize_portion_is_percentage, or
  // a $ amount DEDUCTED from the entry fee (contribution = entry_fee − value)
  // when entry_fee_prize_portion_is_fee_deduction.
  entry_fee_prize_portion?: number | null;
  entry_fee_prize_portion_is_percentage?: boolean;
  entry_fee_prize_portion_is_fee_deduction?: boolean;
  display_prize_to_contestants?: boolean;
  show_prize_amounts_before_complete?: boolean;
  show_prize_amounts_in_results?: boolean;
  default_adjustment_overrides?: Record<
    string,
    { is_active?: boolean; amount?: number | null; percentage?: number | null }
  >;
  /**
   * Per-event judge-sheet template FK. NULL = auto-classify by discipline
   * via the events trigger (barrel_racing / rough_stock / timed).
   */
  judge_sheet_template_id?: string | null;
}

/**
 * One per-performance assignment for an event. `performance_key` links to
 * `PerformanceDraft._key` (client-side UUID). `max_contestants` is an optional
 * per-perf override of the event's rodeo-level contestant_limit; null/undefined
 * inherits the rodeo-level value.
 */
export interface EventPerformanceAssignment {
  performance_key: string;
  max_contestants: number | null;
}

export interface RoundConfigDraft {
  _key: string;
  round_number: number;
  round_name: string;
  performance_key: string;
  is_go_round: boolean;
  is_short_round: boolean;
  advancing_count: number | null;
  /**
   * Average-of-all-rounds payout row. Exactly one per event; non-deletable
   * in the UI (matches `RodeoEventDialogRound.is_average`). Persisted as
   * `metadata.is_average` on rodeo_round_configs so it survives a DB
   * round-trip without a schema change.
   */
  is_average?: boolean;
  // Prize metadata — jackpot-parity, persisted via rodeo_round_configs.metadata.prizes
  has_prizes?: boolean;
  round_prize_total?: number;
  is_progressive?: boolean;
  places?: Array<{ place: number; percentage: number }>;
}

/**
 * Rodeo wizard steps:
 *   1 — Basic Info
 *   2 — Event Configs (rounds are edited inline on the event dialog)
 *   3 — Performances (plus which events run in each)
 *   4 — Review
 */
export type RodeoWizardStep = 1 | 2 | 3 | 4;

interface RodeoWizardState {
  currentStep: RodeoWizardStep;
  draftId: string | null;
  basicInfo: RodeoBasicInfo;
  /**
   * Publish state the user intends to save. Hydrated from the loaded
   * rodeo; flipped by the toggle on the Review step. `saveDraft` writes
   * it as a regular column update, so Save is the one button that can
   * both publish and unpublish — no separate "Save & Publish" button.
   */
  isPublished: boolean;
  performances: PerformanceDraft[];
  events: EventDraft[];
  isSaving: boolean;
  saveError: string | null;
  isLoading: boolean;
}

interface RodeoWizardActions {
  // Navigation
  setStep: (step: RodeoWizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Step 1: Basic Info
  setBasicInfo: (info: Partial<RodeoBasicInfo>) => void;
  /**
   * Flip the wizard's intended publish state. Does NOT hit the backend
   * on its own — the change rides along with the next `saveDraft()`.
   */
  setIsPublished: (value: boolean) => void;

  // Step 2: Performances
  addPerformance: (perf: Omit<PerformanceDraft, '_key'>) => void;
  updatePerformance: (key: string, perf: Partial<PerformanceDraft>) => void;
  removePerformance: (key: string) => void;
  reorderPerformances: (performances: PerformanceDraft[]) => void;

  // Step 3: Event Configs
  addEvent: (config: Omit<EventDraft, '_key' | 'round_configs'>) => void;
  updateEvent: (key: string, config: Partial<EventDraft>) => void;
  removeEvent: (key: string) => void;

  // Step 4: Round Configs (nested under event config)
  addRoundConfig: (eventKey: string, round: Omit<RoundConfigDraft, '_key'>) => void;
  updateRoundConfig: (eventKey: string, roundKey: string, data: Partial<RoundConfigDraft>) => void;
  removeRoundConfig: (eventKey: string, roundKey: string) => void;

  // Draft persistence
  saveDraft: () => Promise<void>;
  loadDraft: (rodeoId: string) => Promise<void>;
  finalizeDraft: () => Promise<void>;

  // Reset
  resetWizard: () => void;
}

type RodeoStore = RodeoWizardState & RodeoWizardActions;

const initialBasicInfo: RodeoBasicInfo = {
  name: '',
  organization_id: '',
  location: '',
  start_date: '',
  end_date: '',
  description: '',
  association: '',
  allow_cash_payment: false,
  allow_pay_later: true,
  is_finals: false,
  member_fee_enabled: null,
  non_member_surcharge: 0,
  preference_config: {
    ifPerformance: [...DEFAULT_PREFERENCE_CONFIG.ifPerformance],
    ifSlack: [...DEFAULT_PREFERENCE_CONFIG.ifSlack],
  },
  entry_close_time: '',
  fees_due_message: '',
  timezone: getDefaultTimezone(),
};

const initialState: RodeoWizardState = {
  currentStep: 1,
  draftId: null,
  basicInfo: { ...initialBasicInfo },
  isPublished: false,
  performances: [],
  events: [],
  isSaving: false,
  saveError: null,
  isLoading: false,
};

const genKey = () => crypto.randomUUID();

const VALID_PREFERENCE_OPTIONS: PreferenceOption[] = ['slack', 'out', 'performance'];

/**
 * Coerce raw `preference_config` from the API (or null) into a fully-populated
 * client object. Filters unknown values and dedupes so a malformed server
 * payload can't break the checkbox UI.
 */
function hydratePreferenceConfig(raw: unknown): PreferenceConfig {
  if (!raw || typeof raw !== 'object') {
    return {
      ifPerformance: [...DEFAULT_PREFERENCE_CONFIG.ifPerformance],
      ifSlack: [...DEFAULT_PREFERENCE_CONFIG.ifSlack],
    };
  }
  const obj = raw as Record<string, unknown>;
  const sanitise = (v: unknown): PreferenceOption[] => {
    if (!Array.isArray(v)) return [...VALID_PREFERENCE_OPTIONS];
    const out: PreferenceOption[] = [];
    for (const item of v) {
      if (
        typeof item === 'string' &&
        (VALID_PREFERENCE_OPTIONS as string[]).includes(item) &&
        !out.includes(item as PreferenceOption)
      ) {
        out.push(item as PreferenceOption);
      }
    }
    return out;
  };
  return {
    ifPerformance: sanitise(obj.ifPerformance),
    ifSlack: sanitise(obj.ifSlack),
  };
}

export const useRodeoStore = create<RodeoStore>()((set, get) => ({
  ...initialState,

  // --- Navigation ---
  setStep: (step) => set({ currentStep: step }),
  nextStep: () => {
    const { currentStep } = get();
    if (currentStep < 4) set({ currentStep: (currentStep + 1) as RodeoWizardStep });
  },
  prevStep: () => {
    const { currentStep } = get();
    if (currentStep > 1) set({ currentStep: (currentStep - 1) as RodeoWizardStep });
  },

  // --- Step 1: Basic Info ---
  setBasicInfo: (info) =>
    set((s) => ({ basicInfo: { ...s.basicInfo, ...info } })),
  setIsPublished: (value) => set({ isPublished: !!value }),

  // --- Step 2: Performances ---
  addPerformance: (perf) =>
    set((s) => ({
      performances: [
        ...s.performances,
        { ...perf, _key: genKey(), _pendingEdit: true },
      ],
    })),

  updatePerformance: (key, perf) =>
    set((s) => ({
      performances: s.performances.map((p) =>
        p._key === key ? { ...p, ...perf, _pendingEdit: true } : p,
      ),
    })),

  removePerformance: (key) =>
    set((s) => {
      const perf = s.performances.find((p) => p._key === key);
      if (!perf) return s;
      // New (unsaved) rows disappear immediately; persisted rows toggle the
      // pending-delete flag so the user can see + undo before Save Draft.
      if (!perf._persisted) {
        return { performances: s.performances.filter((p) => p._key !== key) };
      }
      return {
        performances: s.performances.map((p) =>
          p._key === key ? { ...p, _pendingDelete: !p._pendingDelete } : p,
        ),
      };
    }),

  reorderPerformances: (performances) => set({ performances }),

  // --- Step 3: Event Configs ---
  addEvent: (config) =>
    set((s) => ({
      events: [
        ...s.events,
        { ...config, _key: genKey(), round_configs: [], _pendingEdit: true },
      ],
    })),

  updateEvent: (key, config) =>
    set((s) => ({
      events: s.events.map((c) =>
        c._key === key ? { ...c, ...config, _pendingEdit: true } : c,
      ),
    })),

  removeEvent: (key) =>
    set((s) => {
      const ec = s.events.find((c) => c._key === key);
      if (!ec) return s;
      if (!ec._persisted) {
        return { events: s.events.filter((c) => c._key !== key) };
      }
      return {
        events: s.events.map((c) =>
          c._key === key ? { ...c, _pendingDelete: !c._pendingDelete } : c,
        ),
      };
    }),

  // --- Step 4: Round Configs ---
  addRoundConfig: (eventKey, round) =>
    set((s) => ({
      events: s.events.map((ec) =>
        ec._key === eventKey
          ? { ...ec, round_configs: [...ec.round_configs, { ...round, _key: genKey() }] }
          : ec,
      ),
    })),

  updateRoundConfig: (eventKey, roundKey, data) =>
    set((s) => ({
      events: s.events.map((ec) =>
        ec._key === eventKey
          ? {
              ...ec,
              round_configs: ec.round_configs.map((r) =>
                r._key === roundKey ? { ...r, ...data } : r,
              ),
            }
          : ec,
      ),
    })),

  removeRoundConfig: (eventKey, roundKey) =>
    set((s) => ({
      events: s.events.map((ec) =>
        ec._key === eventKey
          ? { ...ec, round_configs: ec.round_configs.filter((r) => r._key !== roundKey) }
          : ec,
      ),
    })),

  // --- Draft Persistence ---
  saveDraft: async () => {
    const { draftId, basicInfo, performances, events } = get();
    set({ isSaving: true, saveError: null });
    try {
      // Filter out rows the user marked for deletion — these are the source of
      // truth sent to the backend, which does full delete-and-reinsert.
      const keptPerformances = performances.filter((p) => !p._pendingDelete);
      const keptEventConfigs = events.filter((ec) => !ec._pendingDelete);

      // Backend TypeBox schemas use Type.Optional(Type.Number(...)) which
      // accepts `undefined` (omitted) but rejects `null`. The wizard stores
      // `null` for unset numerics (entry_fee, contestant_limit, etc.), so we
      // coerce null → undefined at the wire boundary. JSON.stringify later
      // will drop the undefined keys entirely.
      const nullToUndef = <T>(v: T | null | undefined): T | undefined =>
        v == null ? undefined : v;

      // Backend `contestant_limit` / `default_max_contestants` schemas use
      // `Type.Integer({ minimum: 1 })`, so a stored 0 (or negative) value
      // fails the optional cap as soon as the wizard ships it back. Coerce
      // those non-positive ints to undefined so they're omitted on the wire.
      const positiveOrUndef = (
        v: number | null | undefined,
      ): number | undefined =>
        typeof v === 'number' && v > 0 ? v : undefined;

      const buildPerformances = () =>
        keptPerformances.length
          ? keptPerformances.map((p, i) => ({
              // Ship the DB id for previously persisted rows so the backend
              // merges in place instead of wiping and reinserting. Brand-new
              // rows omit it and the backend allocates a fresh UUID.
              id: p._persisted ? p._key : undefined,
              performance_name: p.performance_name,
              performance_date: p.performance_date || undefined,
              start_time: p.start_time || undefined,
              end_time: p.end_time || undefined,
              sort_order: p.sort_order ?? i + 1,
              is_slack: p.is_slack,
              default_max_contestants: positiveOrUndef(
                p.default_max_contestants,
              ),
            }))
          : undefined;

      // Build event configs with optional performance_id mapping
      const buildEventConfigs = (perfKeyToId?: Map<string, string>) =>
        keptEventConfigs.length
          ? keptEventConfigs.map((ec) => ({
              // Ship the DB id for previously persisted rows so the backend
              // can preserve the events.id that registrations FK to. New
              // rows omit it.
              id: ec._persisted ? ec._key : undefined,
              // Custom display name — normalised to null when blank so the
              // backend's resolver falls back to event_type.
              event_name:
                typeof ec.event_name === 'string' && ec.event_name.trim().length > 0
                  ? ec.event_name.trim()
                  : null,
              event_type: ec.event_type,
              event_category: ec.event_category,
              contestant_limit: positiveOrUndef(ec.contestant_limit),
              // Same positive-int / null-omit semantics as contestant_limit —
              // the backend's CHECK rejects 0/negative.
              contestants_per_performance: positiveOrUndef(ec.contestants_per_performance),
              non_member_surcharge: nullToUndef(ec.non_member_surcharge),
              entry_fee: nullToUndef(ec.entry_fee),
              transaction_fee: nullToUndef(ec.transaction_fee),
              // Persist the actual count of editable rounds so the events
              // table doesn't fall back to its DB default. AVG rows are a
              // scoring-only construct and excluded from the visible round
              // count; the floor of 1 mirrors the setup wizard's minimum.
              total_rounds: Math.max(
                1,
                ec.round_configs.filter((r) => !r.is_average).length,
              ),
              // Multi-registration cap. Both fields are optional on the
              // backend schema; omit them when the toggle is off so legacy
              // rodeos stay untouched. When on we always ship a positive int
              // (defaulting to 1 client-side already so `?? 1` is just
              // belt-and-suspenders).
              allow_multiple_registrations: ec.allow_multiple_registrations
                ? true
                : undefined,
              max_registrations_per_user: ec.allow_multiple_registrations
                ? Math.max(1, ec.max_registrations_per_user ?? 1)
                : undefined,
              // Always ship the balance flag when multi-registration is on so
              // turning it back OFF persists (omitting it would leave a stale
              // `true` on the events row).
              enforce_balanced_positions: ec.allow_multiple_registrations
                ? !!ec.enforce_balanced_positions
                : undefined,
              // Pay Later override. Always shipped — including as an explicit
              // null — so switching the picker back to "Inherit" clears a
              // previously-saved override instead of leaving it stuck.
              allow_pay_later:
                typeof ec.allow_pay_later === 'boolean' ? ec.allow_pay_later : null,
              // Resolve each client-side perf key to its DB id. Drop any
              // assignment whose perf hasn't been persisted yet (shouldn't
              // happen since perfs always save first, but defensive).
              performance_assignments: (ec.performance_assignments ?? [])
                .map((a) => {
                  const pid = perfKeyToId?.get(a.performance_key);
                  if (!pid) return null;
                  return {
                    performance_id: pid,
                    max_contestants: a.max_contestants ?? null,
                  };
                })
                .filter((x): x is { performance_id: string; max_contestants: number | null } => x !== null),
              // Prize data (total + adjustments) rides in metadata.prizes.
              metadata: {
                prizes: {
                  total_prize_amount: ec.total_prize_amount ?? null,
                  adjustments: ec.prize_adjustments ?? [],
                },
              },
              // Dynamic prize allocation + contestant display (first-class
              // events columns, persisted by rodeoCreationService.saveEvents).
              prize_allocation_mode:
                ec.prize_allocation_mode === 'dynamic' ? 'dynamic' : 'fixed',
              entry_fee_prize_portion: ec.entry_fee_prize_portion ?? 0,
              entry_fee_prize_portion_is_percentage:
                ec.entry_fee_prize_portion_is_percentage === true,
              entry_fee_prize_portion_is_fee_deduction:
                ec.entry_fee_prize_portion_is_fee_deduction === true,
              display_prize_to_contestants: ec.display_prize_to_contestants === true,
              show_prize_amounts_before_complete:
                ec.show_prize_amounts_before_complete === true,
              // Master toggle defaults ON when omitted by the caller.
              show_prize_amounts_in_results:
                ec.show_prize_amounts_in_results !== false,
              default_adjustment_overrides: ec.default_adjustment_overrides ?? {},
              // Per-event judge-sheet template override. Omit when null so
              // the schema's optional gate is satisfied; the trigger then
              // auto-classifies on insert.
              judge_sheet_template_id: ec.judge_sheet_template_id ?? undefined,
              round_configs: ec.round_configs.length
                ? ec.round_configs.map((r) => ({
                    round_number: r.round_number,
                    round_name: r.round_name || undefined,
                    performance_id: perfKeyToId?.get(r.performance_key) || undefined,
                    is_go_round: r.is_go_round,
                    is_short_round: r.is_short_round,
                    // Backend schema requires >= 1 when present; treat 0/null
                    // as "no advancing" (everyone advances) and omit.
                    advancing_count:
                      r.advancing_count && r.advancing_count > 0
                        ? r.advancing_count
                        : undefined,
                    // Per-round prize config + is_average flag ride in metadata.
                    metadata: {
                      is_average: !!r.is_average,
                      prizes: {
                        has_prizes: !!r.has_prizes,
                        round_prize_total: Number(r.round_prize_total) || 0,
                        is_progressive: !!r.is_progressive,
                        places: r.places ?? [],
                      },
                    },
                  }))
                : undefined,
            }))
          : undefined;

      if (draftId) {
        // ── Update existing draft ──

        // 1. Update basic info via PUT
        const { isPublished } = get();
        // Anchor the wall-clock entry-close to the rodeo's saved timezone
        // before sending so timestamptz storage is unambiguous. Skipping
        // the field when the picker is blank keeps partial drafts saveable.
        const entryCloseUtc = basicInfo.entry_close_time
          ? localInputToUtcIso(
              basicInfo.entry_close_time,
              basicInfo.timezone,
            )
          : null;
        const basicPayload: Record<string, unknown> = {
          name: basicInfo.name,
          primary_organization_id: basicInfo.organization_id || undefined,
          start_date: basicInfo.start_date || undefined,
          end_date: basicInfo.end_date || undefined,
          location: basicInfo.location || undefined,
          description: basicInfo.description || undefined,
          association: basicInfo.association || undefined,
          allow_cash_payment: basicInfo.allow_cash_payment,
          allow_pay_later: basicInfo.allow_pay_later,
          is_finals: basicInfo.is_finals,
          // Explicit null is meaningful — it clears the override so the rodeo
          // inherits the org again. Sent verbatim, never coerced.
          member_fee_enabled: basicInfo.member_fee_enabled,
          // FIX: Use nullish coalescing — 0 is a valid surcharge value
          non_member_surcharge: basicInfo.non_member_surcharge,
          preference_config: basicInfo.preference_config,
          // entry_close_time is required at the schema layer; only ship it
          // when the user has filled it in so partial drafts don't 400 on save.
          entry_close_time: entryCloseUtc ?? undefined,
          timezone: basicInfo.timezone,
          // Empty string is valid on the wire — backend trims+nullifies it.
          // null explicitly clears a previously-set message.
          fees_due_message: basicInfo.fees_due_message.trim() === ''
            ? null
            : basicInfo.fees_due_message,
          // Publish state rides along with the basic-info save so Save
          // can both publish and unpublish in one click.
          is_published: isPublished,
        };
        const cleanBasic = JSON.parse(JSON.stringify(basicPayload));
        await httpClient.put(`/jackpot-ops/rodeo/${draftId}`, cleanBasic);

        // 2. Save performances (always POST in edit mode so deletions persist —
        //    the endpoint does delete-and-reinsert, so an empty array wipes all).
        const perfs = buildPerformances() ?? [];
        const perfKeyToId = new Map<string, string>();
        const perfResult = await httpClient.post<{ performances: { id: string }[] }>(
          `/jackpot-ops/rodeo/${draftId}/performances`,
          { performances: perfs },
        );
        const savedPerfs = perfResult.performances || [];
        keptPerformances.forEach((p, i) => {
          if (savedPerfs[i]) {
            perfKeyToId.set(p._key, savedPerfs[i].id);
          }
        });

        // 3. Save event configs with performance_id associations (always POST).
        const ecs = buildEventConfigs(perfKeyToId) ?? [];
        const ecsClean = JSON.parse(JSON.stringify(ecs));
        // Strip round_configs from event config payloads — they are saved separately
        const ecsWithoutRounds = ecsClean.map(
          ({ round_configs: _rc, ...rest }: any) => rest,
        );
        const ecResult = await httpClient.post<{ events: { id: string }[] }>(
          `/jackpot-ops/rodeo/${draftId}/events`,
          { events: ecsWithoutRounds },
        );

        // 4. Save round configs for each event config (with performance_id)
        const savedConfigs = ecResult.events || [];
        for (let i = 0; i < keptEventConfigs.length; i++) {
          const ec = keptEventConfigs[i];
          const savedEc = savedConfigs[i];
          if (savedEc && ec.round_configs.length > 0) {
            await httpClient.post(`/jackpot-ops/rodeo/${draftId}/round-configs`, {
              event_id: savedEc.id,
              round_configs: ec.round_configs.map((r) => ({
                round_number: r.round_number,
                round_name: r.round_name || undefined,
                performance_id: perfKeyToId.get(r.performance_key) || undefined,
                is_go_round: r.is_go_round,
                is_short_round: r.is_short_round,
                advancing_count:
                  r.advancing_count && r.advancing_count > 0
                    ? r.advancing_count
                    : undefined,
                metadata: {
                  is_average: !!r.is_average,
                  prizes: {
                    has_prizes: !!r.has_prizes,
                    round_prize_total: Number(r.round_prize_total) || 0,
                    is_progressive: !!r.is_progressive,
                    places: r.places ?? [],
                  },
                },
              })),
            });
          }
        }

        // 5. Sync store state with what's actually in the DB now: drop
        //    pending-delete rows, mark remaining rows as persisted, and clear
        //    the pending-edit flag since their changes just round-tripped.
        set({
          performances: keptPerformances.map((p) => ({
            ...p,
            _persisted: true,
            _pendingDelete: false,
            _pendingEdit: false,
          })),
          events: keptEventConfigs.map((ec) => ({
            ...ec,
            _persisted: true,
            _pendingDelete: false,
            _pendingEdit: false,
          })),
        });
      } else {
        // ── Create new draft sequentially so we can resolve performance IDs ──
        // Anchor the wall-clock entry-close to the wizard's selected timezone
        // before sending so the row's timestamptz column stores an unambiguous
        // UTC instant.
        const entryCloseUtcCreate =
          localInputToUtcIso(basicInfo.entry_close_time, basicInfo.timezone) ??
          basicInfo.entry_close_time;
        const draftPayload: Record<string, unknown> = {
          name: basicInfo.name,
          primary_organization_id: basicInfo.organization_id || undefined,
          start_date: basicInfo.start_date || undefined,
          end_date: basicInfo.end_date || undefined,
          location: basicInfo.location || undefined,
          description: basicInfo.description || undefined,
          association: basicInfo.association || undefined,
          allow_cash_payment: basicInfo.allow_cash_payment,
          allow_pay_later: basicInfo.allow_pay_later,
          is_finals: basicInfo.is_finals,
          member_fee_enabled: basicInfo.member_fee_enabled,
          non_member_surcharge: basicInfo.non_member_surcharge,
          preference_config: basicInfo.preference_config,
          // Required on create — RodeoWizard's Save button is disabled when
          // missingSteps includes this field, so a non-empty string is
          // guaranteed by the time we land here.
          entry_close_time: entryCloseUtcCreate,
          timezone: basicInfo.timezone,
          fees_due_message: basicInfo.fees_due_message.trim() === ''
            ? null
            : basicInfo.fees_due_message,
        };
        const cleanDraft = JSON.parse(JSON.stringify(draftPayload));

        // 1. Create basic draft
        const result = await httpClient.post<{ id: string } | { rodeo: { id: string } }>(
          '/jackpot-ops/rodeo/draft',
          cleanDraft,
        );
        const newId = (result as any).rodeo?.id ?? (result as any).id;
        set({ draftId: newId });

        // 2. Save performances and build key→id mapping
        const perfs = buildPerformances();
        const newPerfKeyToId = new Map<string, string>();
        if (perfs) {
          const perfResult = await httpClient.post<{ performances: { id: string }[] }>(
            `/jackpot-ops/rodeo/${newId}/performances`,
            { performances: perfs },
          );
          const savedPerfs = perfResult.performances || [];
          performances.forEach((p, i) => {
            if (savedPerfs[i]) {
              newPerfKeyToId.set(p._key, savedPerfs[i].id);
            }
          });
        }

        // 3. Save event configs with performance_id associations
        const ecs = buildEventConfigs(newPerfKeyToId);
        if (ecs) {
          const ecsClean = JSON.parse(JSON.stringify(ecs));
          const ecsWithoutRounds = ecsClean.map(
            ({ round_configs: _rc, ...rest }: any) => rest,
          );
          const ecResult = await httpClient.post<{ events: { id: string }[] }>(
            `/jackpot-ops/rodeo/${newId}/events`,
            { events: ecsWithoutRounds },
          );

          // 4. Save round configs with performance_id
          const savedConfigs = ecResult.events || [];
          for (let i = 0; i < events.length; i++) {
            const ec = events[i];
            const savedEc = savedConfigs[i];
            if (savedEc && ec.round_configs.length > 0) {
              await httpClient.post(`/jackpot-ops/rodeo/${newId}/round-configs`, {
                event_id: savedEc.id,
                round_configs: ec.round_configs.map((r) => ({
                  round_number: r.round_number,
                  round_name: r.round_name || undefined,
                  performance_id: newPerfKeyToId.get(r.performance_key) || undefined,
                  is_go_round: r.is_go_round,
                  is_short_round: r.is_short_round,
                  advancing_count:
                    r.advancing_count && r.advancing_count > 0
                      ? r.advancing_count
                      : undefined,
                  metadata: {
                    is_average: !!r.is_average,
                    prizes: {
                      has_prizes: !!r.has_prizes,
                      round_prize_total: Number(r.round_prize_total) || 0,
                      is_progressive: !!r.is_progressive,
                      places: r.places ?? [],
                    },
                  },
                })),
              });
            }
          }
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save rodeo draft';
      set({ saveError: msg });
      throw err;
    } finally {
      set({ isSaving: false });
    }
  },

  loadDraft: async (rodeoId) => {
    set({ isLoading: true, saveError: null });
    try {
      const data = await httpClient.get<any>(`/jackpot-ops/rodeo/${rodeoId}/config`);

      const rodeo = data.rodeo || data;
      const perfList = data.performances || [];
      const ecList = data.events || [];

      // `association` still lives inside metadata JSONB. `location` was
      // promoted to a real top-level column by the
      // add_location_to_rodeos migration; keep the metadata fallback so
      // any draft that survived from before the backfill still loads
      // its venue.
      const meta = (rodeo.metadata as Record<string, unknown>) || {};

      // Hydrate the rodeo's saved timezone first — every wall-clock field
      // hydrated below has to be split in this zone for the picker to show
      // the same time the admin entered. Falls back to the user's browser
      // default when the column is null on a legacy row.
      const loadedTimezone =
        (typeof rodeo.timezone === 'string' && rodeo.timezone.trim().length > 0)
          ? (rodeo.timezone as UsTimezone)
          : getDefaultTimezone();
      set({
        draftId: rodeo.id || rodeoId,
        isPublished: !!rodeo.is_published,
        basicInfo: {
          name: rodeo.name || '',
          organization_id: rodeo.organization_id || rodeo.primary_organization_id || '',
          location:
            (typeof rodeo.location === 'string' && rodeo.location) ||
            (meta.location as string) ||
            '',
          start_date: rodeo.start_date || '',
          end_date: rodeo.end_date || '',
          description: rodeo.description || '',
          association: (meta.association as string) || '',
          allow_cash_payment: !!rodeo.allow_cash_payment,
          // Column default is true — only an explicit false turns it off, so
          // a legacy row without the column still reads as "Pay Later on".
          allow_pay_later: rodeo.allow_pay_later !== false,
          // Column default is false — only an explicit true marks a finals.
          is_finals: rodeo.is_finals === true,
          // Tri-state: keep null (inherit org) distinct from an explicit false.
          member_fee_enabled:
            typeof rodeo.member_fee_enabled === 'boolean'
              ? rodeo.member_fee_enabled
              : null,
          non_member_surcharge: rodeo.non_member_surcharge != null ? Number(rodeo.non_member_surcharge) : 0,
          preference_config: hydratePreferenceConfig(rodeo.preference_config),
          // Backend returns entry_close_time as a full ISO timestamp.
          // <input type="datetime-local"> needs "YYYY-MM-DDTHH:mm" pinned to
          // the rodeo's saved timezone so the picker shows the wall-clock
          // the admin originally entered, regardless of viewer locale.
          entry_close_time: utcIsoToLocalInput(
            rodeo.entry_close_time,
            loadedTimezone,
          ),
          fees_due_message: rodeo.fees_due_message || '',
          timezone: loadedTimezone,
        },
        performances: perfList.map((p: any, i: number) => ({
          _key: p.id || genKey(),
          _persisted: true,
          performance_name: p.performance_name || '',
          performance_date: p.performance_date || '',
          start_time: p.start_time || '',
          end_time: p.end_time || '',
          sort_order: p.sort_order ?? i + 1,
          is_slack: !!p.is_slack,
          default_max_contestants: p.default_max_contestants ?? null,
        })),
        events: ecList.map((ec: any) => {
          const ecPrizes = (ec?.metadata?.prizes ?? {}) as {
            total_prize_amount?: number | null;
            adjustments?: Array<{
              id?: string;
              operation: 'add' | 'subtract';
              adjustment_type: 'amount' | 'percentage';
              amount?: number;
              percentage?: number;
              reason: string;
            }>;
          };
          // Rehydrate perf assignments from the junction. The backend returns
          // performance_id (DB UUID); we store performance_key on the client
          // using the same UUIDs so hydrated perfs and fresh perfs coexist.
          const assignments: EventPerformanceAssignment[] = Array.isArray(
            ec.performance_assignments,
          )
            ? ec.performance_assignments.map((a: any) => ({
                performance_key: a.performance_id,
                max_contestants: a.max_contestants ?? null,
              }))
            : [];
          return {
            _key: ec.id || genKey(),
            _persisted: true,
            event_name: typeof ec.event_name === 'string' ? ec.event_name : '',
            event_type: ec.event_type || '',
            event_category: ec.event_category || 'timed',
            contestant_limit: ec.contestant_limit ?? null,
            // Rehydrate the per-event default cap so reopening the wizard
            // shows what was saved.
            contestants_per_performance:
              (ec as any).contestants_per_performance ?? null,
            non_member_surcharge: ec.non_member_surcharge ?? null,
            entry_fee: ec.entry_fee ?? null,
            transaction_fee: ec.transaction_fee ?? null,
            allow_multiple_registrations: !!ec.allow_multiple_registrations,
            max_registrations_per_user: ec.max_registrations_per_user ?? null,
            enforce_balanced_positions: !!ec.enforce_balanced_positions,
            // Tri-state — null means "inherit the rodeo", which must survive
            // the round-trip rather than collapsing to false.
            allow_pay_later:
              typeof (ec as any).allow_pay_later === 'boolean'
                ? (ec as any).allow_pay_later
                : null,
            performance_assignments: assignments,
            judge_sheet_template_id: (ec as any).judge_sheet_template_id ?? null,
            // Dynamic prize allocation + display flags — the save payload
            // sends these, so hydrate them or Save resets the event to fixed.
            prize_allocation_mode:
              (ec as any).prize_allocation_mode === 'dynamic' ? 'dynamic' : 'fixed',
            entry_fee_prize_portion: Number((ec as any).entry_fee_prize_portion) || 0,
            entry_fee_prize_portion_is_percentage:
              (ec as any).entry_fee_prize_portion_is_percentage === true,
            entry_fee_prize_portion_is_fee_deduction:
              (ec as any).entry_fee_prize_portion_is_fee_deduction === true,
            display_prize_to_contestants: (ec as any).display_prize_to_contestants === true,
            show_prize_amounts_before_complete:
              (ec as any).show_prize_amounts_before_complete === true,
            show_prize_amounts_in_results:
              (ec as any).show_prize_amounts_in_results !== false,
            default_adjustment_overrides:
              (ec as any).default_adjustment_overrides &&
              typeof (ec as any).default_adjustment_overrides === 'object'
                ? (ec as any).default_adjustment_overrides
                : {},
            total_prize_amount: ecPrizes.total_prize_amount ?? null,
            prize_adjustments: (ecPrizes.adjustments ?? []).map((a) => ({
              id: a.id ?? genKey(),
              operation: a.operation,
              adjustment_type: a.adjustment_type,
              amount: Number(a.amount) || 0,
              percentage: Number(a.percentage) || 0,
              reason: a.reason,
            })),
            round_configs: (ec.round_configs || []).map((r: any) => {
              const rMeta = (r?.metadata ?? {}) as Record<string, any>;
              const rPrizes = (rMeta.prizes ?? {}) as {
                has_prizes?: boolean;
                round_prize_total?: number;
                is_progressive?: boolean;
                places?: Array<{ place: number; percentage: number }>;
              };
              return {
                _key: r.id || genKey(),
                round_number: r.round_number,
                round_name: r.round_name || '',
                performance_key: r.performance_id || '',
                is_go_round: !!r.is_go_round,
                is_short_round: !!r.is_short_round,
                advancing_count: r.advancing_count ?? null,
                is_average: !!rMeta.is_average,
                has_prizes: !!rPrizes.has_prizes,
                round_prize_total: Number(rPrizes.round_prize_total) || 0,
                is_progressive: !!rPrizes.is_progressive,
                places: Array.isArray(rPrizes.places) ? rPrizes.places : [],
              };
            }),
          };
        }),
        currentStep: 1,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load rodeo';
      set({ saveError: msg });
    } finally {
      set({ isLoading: false });
    }
  },

  finalizeDraft: async () => {
    const { draftId } = get();
    if (!draftId) throw new Error('No draft to finalize');
    set({ isSaving: true, saveError: null });
    try {
      await httpClient.put(`/jackpot-ops/rodeo/${draftId}/finalize`, {});
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to publish rodeo';
      set({ saveError: msg });
      throw err;
    } finally {
      set({ isSaving: false });
    }
  },

  // --- Reset ---
  resetWizard: () => set({ ...initialState }),
}));

// Selector hooks
export const useRodeoCurrentStep = () => useRodeoStore((s) => s.currentStep);
export const useRodeoBasicInfo = () => useRodeoStore((s) => s.basicInfo);
export const useRodeoPerformances = () => useRodeoStore((s) => s.performances);
export const useRodeoEvents = () => useRodeoStore((s) => s.events);
export const useRodeoSaving = () =>
  useRodeoStore((s) => ({ isSaving: s.isSaving, saveError: s.saveError }));
